import { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { useUser, TournamentContext } from "./contexts";

const DEFAULT_TOURNAMENT_REWARDS = {
    minPlayers: 3,
    participationBonus: 0,
    firstPlaceReward: 150,
    secondPlaceReward: 100,
    thirdPlaceReward: 50,
};

export const TournamentProvider = ({ children }) => {
    const { preferences } = useUser();
    
    // Tournament rewards configuration from siteSettings
    const tournamentRewards = preferences?.siteSettings?.tournamentRewards ?? DEFAULT_TOURNAMENT_REWARDS;

    // Calculate total prize pool for a tournament with given number of participants
    const calculatePrizePool = useCallback((participantCount) => {
        if (participantCount < tournamentRewards.minPlayers) {
            return 0;
        }
        
        const pool =
            tournamentRewards.firstPlaceReward +
            tournamentRewards.secondPlaceReward +
            tournamentRewards.thirdPlaceReward +
            (tournamentRewards.participationBonus * participantCount);
        
        return pool;
    }, [tournamentRewards]);

    // Get reward for a specific placement
    const getRewardForPlacement = useCallback((placement, isParticipant = true) => {
        if (!isParticipant) {
            return tournamentRewards.participationBonus;
        }

        switch (placement) {
            case 1:
                return tournamentRewards.firstPlaceReward;
            case 2:
                return tournamentRewards.secondPlaceReward;
            case 3:
                return tournamentRewards.thirdPlaceReward;
            default:
                return tournamentRewards.participationBonus;
        }
    }, [tournamentRewards]);

    // Check if tournament can be started with given participant count
    const canStartTournament = useCallback((participantCount) => {
        return participantCount >= tournamentRewards.minPlayers;
    }, [tournamentRewards.minPlayers]);

    const value = useMemo(() => ({
        tournamentRewards,
        calculatePrizePool,
        getRewardForPlacement,
        canStartTournament,
    }), [tournamentRewards, calculatePrizePool, getRewardForPlacement, canStartTournament]);

    return (
        <TournamentContext.Provider value={value}>
            {children}
        </TournamentContext.Provider>
    );
};

TournamentProvider.propTypes = {
    children: PropTypes.node.isRequired,
};
